import React from "react";
import { View } from "react-native-web";
import CustomText from "../atoms/CustomText";
import { colors, spacing } from "../../utils/styling";
import { useResponsive } from "../../hooks/useResponsive";
import * as Toast from '@radix-ui/react-toast';
import CircularProgress from "@mui/material/CircularProgress";

interface PaywallOverlayProps {
  isVisible: boolean;
  onUnlock: (redeemed: boolean) => void;
  children: React.ReactNode;
}

// Stub for coupon code validation
function checkValidCode(code: string): boolean {
  // TODO: Replace with real logic
  return code.trim().toLowerCase() === 'secretcode1234';
}

export default function PaywallOverlay({
  isVisible,
  onUnlock,
  children,
}: PaywallOverlayProps) {
  const { isMobile } = useResponsive();
  const [coupon, setCoupon] = React.useState("");
  const [error, setError] = React.useState("");
  const [clicked, setClicked] = React.useState(false);
  const [openToast, setOpenToast] = React.useState(false);

  const pdfFeatures = [
    {
      title: '📄 Downloadable Personality PDF',
      description: 'Full breakdown of your money personality, strengths, weaknesses, and unique traits.'
    },
    {
      title: '⏱️ 5-Step Guided Action Plan',
      description: 'Practical, tailored steps to grow based on your dominant personality.'
    },
    {
      title: '🧠 In-Depth Insights',
      description: 'Learn how your personality affects decisions, relationships, and financial habits.'
    },
    {
      title: '📬 Daily Financial Tips',
      description: 'From real financial professionals—delivered right to your inbox.'
    },
    {
      title: '📄 Monthly Newsletters',
      description: 'Stay in the loop with trends, tools, and empowering money strategies.'
    },
    {
      title: '📚 Exclusive Resources',
      description: 'Worksheets, tools, and content to level up your financial literacy.'
    },
  ];

  // Security measures to prevent bypass
  React.useEffect(() => {
    if (!isVisible) return;

    const preventBypass = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    const preventRightClick = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    const preventKeyboard = (e: KeyboardEvent) => {
      // Prevent F12, Ctrl+Shift+I, Ctrl+U, etc.
      if (
        e.key === "F12" ||
        (e.ctrlKey && e.shiftKey && e.key === "I") ||
        (e.ctrlKey && e.key === "u") ||
        (e.ctrlKey && e.key === "U") ||
        (e.ctrlKey && e.shiftKey && e.key === "C") ||
        (e.ctrlKey && e.key === "s") ||
        (e.ctrlKey && e.key === "S")
      ) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    // Add event listeners
    document.addEventListener("contextmenu", preventRightClick);
    document.addEventListener("keydown", preventKeyboard);
    document.addEventListener("selectstart", preventBypass);
    document.addEventListener("dragstart", preventBypass);
    document.addEventListener("copy", preventBypass);
    document.addEventListener("cut", preventBypass);
    document.addEventListener("paste", preventBypass);

    // Cleanup
    return () => {
      document.removeEventListener("contextmenu", preventRightClick);
      document.removeEventListener("keydown", preventKeyboard);
      document.removeEventListener("selectstart", preventBypass);
      document.removeEventListener("dragstart", preventBypass);
      document.removeEventListener("copy", preventBypass);
      document.removeEventListener("cut", preventBypass);
      document.removeEventListener("paste", preventBypass);
    };
  }, [isVisible]);

  if (!isVisible) {
    return <>{children}</>;
  }

  const handleUnlock = () => {
    // any other logic when user clicks "unlock results"
    onUnlock(false); // user did not redeem
  }

  const handleRedeem = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setClicked(true);
    setTimeout(() => {
      setClicked(false);
      if (checkValidCode(coupon)) {
        setOpenToast(true);
        setTimeout(() => {
          onUnlock(true); // user did redeem
        }, 1200);
      } else {
        setError("Invalid coupon code, please try again.");
      }
    }, 1000);
  };

  return (
    <Toast.Provider swipeDirection="right">
      <View style={{ 
        position: "relative", 
        width: "100%",
        overflow: "hidden",
        borderRadius: 16,
        border: 'none',
        }}>
        {/* Content with blur effect */}
        <View
          className="paywall-content"
          style={{
            filter: "blur(8px)",
            WebkitFilter: "blur(8px)",
            MozFilter: "blur(8px)",
            msFilter: "blur(8px)",
            pointerEvents: "none",
            position: "relative",
            zIndex: 1,
            // Prevent text selection and copying
            WebkitTouchCallout: "none",
            WebkitUserSelect: "none",
            KhtmlUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none",
            userSelect: "none",
            // Prevent context menu
            WebkitContextMenu: "none",
            // Prevent drag and drop
            WebkitUserDrag: "none",
            KhtmlUserDrag: "none",
            MozUserDrag: "none",
            OUserDrag: "none",
            userDrag: "none",
          }}
        >
          {children}
        </View>

        {/* Overlay with gradient and lock icon */}
        <View
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            margin: '0 auto',
            maxWidth: 1400,
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.25) 100%)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            zIndex: 10,
            padding: spacing.xl,
            borderRadius: 16,
            border: "3px solid rgba(0,0,0,0.1)",
          }}
        >
          {/* Lock Icon */}
          <View
            style={{
              width: isMobile ? 80 : 120,
              height: isMobile ? 80 : 120,
              backgroundColor: colors.primary,
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginBottom: spacing.lg,
            }}
          >
            <svg
              width={isMobile ? 40 : 60}
              height={isMobile ? 40 : 60}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19 11H5C3.89543 11 3 11.8954 3 13V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V13C21 11.8954 20.1046 11 19 11Z"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </View>

          {/* Title */}
          <CustomText
            style={{
              fontSize: isMobile ? 28 : 36,
              fontWeight: "700",
              color: colors.dark,
              fontFamily: "Nunito",
              textAlign: "center",
              marginBottom: spacing.md,
            }}
          >
            Unlock the Full Power of Your Money Personality
          </CustomText>

          {/* Description */}
          <CustomText
            style={{
              fontSize: isMobile ? 18 : 24,
              color: colors.gray[700],
              fontFamily: "Nunito",
              textAlign: "center",
              marginBottom: spacing.xl,
              maxWidth: 400,
            }}
          >
            For less than the price of a coffee, get the tools to transform your 
            financial mindset.
          </CustomText>

          {/* Content Area */}
          <View style={{
            border: '3px solid black',
            backgroundColor: '#fff7da',
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? 5 : 10,
            padding: isMobile ? 12 : 20,
            borderRadius: 12,
            marginBottom: spacing.xl,
            width: '100%',
            maxWidth: 850,
            boxSizing: 'border-box',
            overflow: 'hidden',
            position: 'relative',
          }}>
            {pdfFeatures.map((feature, idx) => (
              <View 
                key={idx} 
                style={{
                  display: 'flex', 
                  flexDirection: 'row', 
                  alignItems: 'flex-start', 
                  gap: 12, 
                  marginBottom: isMobile ? 6 : 10,
                  width: '100%',
                }}
              >
                <View style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  width: '100%',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}>
                  <CustomText style={{ 
                    fontWeight: 700, 
                    fontSize: isMobile ? 18 : 20, 
                    fontFamily: 'Nunito', 
                    width: '100%',
                    color: colors.dark,
                    textAlign: 'center'
                  }}>
                    {feature.title}
                  </CustomText>
                  <CustomText style={{ 
                    fontWeight: 400, 
                    fontSize: isMobile ? 15 : 17, 
                    fontFamily: 'Nunito', 
                    color: colors.gray[800],
                    width: '100%',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    textAlign: 'center'
                  }}>
                    {feature.description}
                  </CustomText>
                </View>
              </View>
            ))}
          </View>

          <CustomText style={{
            fontFamily: "Nunito",
            fontWeight: 700,
            fontSize: isMobile ? 18 : 24,
            textAlign: 'center',
            marginBottom: isMobile ? spacing.sm : spacing.md,
          }}>
            <i> All of This for less Than a Latte. </i>
          </CustomText>
          <CustomText style={{
            fontFamily: "Nunito",
            fontWeight: 400,
            fontSize: isMobile ? 18 : 24,
            textAlign: 'center',
            marginBottom: isMobile ? spacing.md : spacing.lg,
          }}>
            One-time Fee of $6.99 / $4.99 per month
          </CustomText>

          {/* Coupon code input */}
          <form 
            onSubmit={handleRedeem} 
            style={{ 
              width: '100%', 
              maxWidth: 400, 
              marginBottom: isMobile ? spacing.sm : spacing.md, 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row', 
              gap: spacing.sm, 
              alignItems: 'center', 
              justifyContent: 'center' }
          }>
            <input
              type="text"
              placeholder="Enter coupon code"
              value={coupon}
              onChange={e => setCoupon(e.target.value)}
              style={{
                flex: 1,
                padding: isMobile ? spacing.sm : spacing.md,
                fontSize: isMobile ? 16 : 18,
                borderRadius: 8,
                border: `1px solid ${colors.gray[300]}`,
                fontFamily: 'Nunito',
                outline: 'none',
              }}
            />
            <button
              type="submit"
              style={{
                minWidth: isMobile ? 200 : 0,
                height: '100%',
                minHeight: 'initial',
                flex: 1,
                background: colors.primary,
                color: 'white',
                border: 'none',
                borderRadius: 999,
                padding: spacing.sm,
                fontFamily: 'Nunito',
                fontSize: isMobile ? 18 : 24,
                fontWeight: 600,
                cursor: 'pointer',
                marginLeft: isMobile ? 0 : spacing.sm,
                marginTop: isMobile ? spacing.sm : 0,
                marginBottom: isMobile ? spacing.sm : 0,
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {!clicked ? "Redeem" : <CircularProgress size={24} color="inherit" />}
              {/* <CircularProgress size={22} color="inherit" /> */}
            </button>
          </form>
          {error && (
            <CustomText style={{ 
              color: colors.danger, 
              fontSize: isMobile ? 16 : 20, 
              marginBottom: isMobile ? spacing.md : spacing.lg, 
              textAlign: 'center'
            }}>
              {error}
            </CustomText>
          )}

          <CustomText style={{
            fontFamily: "Nunito",
            fontWeight: 700,
            fontSize: isMobile ? 18 : 24,
            marginBottom: isMobile ? spacing.sm : spacing.md,
            textAlign: 'center',
          }}>
            OR
          </CustomText>

          {/* Unlock Button */}
          <button
            onClick={handleUnlock}
            style={{
              background: "black",
              color: "white",
              border: "none",
              borderRadius: 50,
              padding: isMobile ? `${spacing.md}px ${spacing.xl}px` : `${spacing.md}px ${spacing.xl * 2}px`,
              fontFamily: "Nunito",
              fontSize: isMobile ? 18 : 20,
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.3s ease",
              minWidth: 200,
              marginBottom: isMobile ? spacing.md : spacing.lg,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            Get My Full Money Personality Package
          </button>

          {/* Additional security note */}
          <CustomText
            style={{
              fontSize: isMobile ? 12 : 14,
              marginBottom: isMobile ? spacing.sm : spacing.md,
              color: colors.gray[500],
              fontFamily: "Nunito",
              textAlign: "center",
              fontStyle: "italic",
            }}
          >
            Secure payment • Instant access
          </CustomText>
          <CustomText
            style={{
              fontSize: isMobile ? 8 : 12,
              color: colors.dark,
              fontFamily: "Nunito",
              textAlign: "center",
              fontStyle: "italic",
              width: isMobile ? '100%' : '50%',
            }}
          >
            Cancel anytime. You can cancel your subscription at any time from your account settings.
            Your access will continue until the end of your current billing period. No refunds will be
            issued for partial periods.
          </CustomText>
        </View>

        {/* Additional security layer - invisible overlay to prevent interaction */}
        <View
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 5,
            pointerEvents: "auto",
            // Additional security measures
            WebkitTouchCallout: "none",
            WebkitUserSelect: "none",
            KhtmlUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none",
            userSelect: "none",
            // Prevent any interaction with underlying content
            cursor: "default",
          }}
        />

        {/* CSS-in-JS security styles */}
        <style
          dangerouslySetInnerHTML={{
            __html: `
          @media print {
            .paywall-content { display: none !important; }
          }
          .paywall-content {
            -webkit-filter: blur(8px) !important;
            -moz-filter: blur(8px) !important;
            -ms-filter: blur(8px) !important;
            filter: blur(8px) !important;
            pointer-events: none !important;
            user-select: none !important;
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            -webkit-touch-callout: none !important;
            -webkit-user-drag: none !important;
            -khtml-user-drag: none !important;
            -moz-user-drag: none !important;
            -o-user-drag: none !important;
            user-drag: none !important;
          }
                `,
          }}
        />
      </View>
      <Toast.Root open={openToast} onOpenChange={setOpenToast} duration={1500} style={{ background: colors.success, color: 'white', borderRadius: 8, padding: 16, fontFamily: 'Nunito', fontSize: 18, boxShadow: '0 2px 10px rgba(0,0,0,0.15)' }}>
        Coupon code applied! Your results are now unlocked.
      </Toast.Root>
      <Toast.Viewport style={{ position: 'fixed', bottom: 40, left: 0, right: 0, display: 'flex', justifyContent: 'center', zIndex: 9999 }} />
    </Toast.Provider>
  );
}
